import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PublicNavbar from '../components/public/PublicNavbar';
import PublicFooter from '../components/public/PublicFooter';

const LAST_UPDATED_LABEL = 'March 17, 2026';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation('legal');
  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';

    document.title = 'Privacy Policy | Hairchitech';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Read Hairchitech privacy policy covering data collection, AI processing, cookies, retention, and user rights.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-transparent relative text-white overflow-hidden">
      <PublicNavbar />

      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800" />
      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }} />
      <div
        className="absolute top-0 right-0 h-[150%] w-1 z-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom left, rgba(201,160,56,0.3) 0%, transparent 80%)',
          transform: 'translate(50%, -50%) rotate(45deg)',
          transformOrigin: 'top right'
        }}
      />
      <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }} />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }} />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
      <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.3) 0%, transparent 100%)' }} />
      <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }} />

      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-14">
          <span
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border"
            style={{ borderColor: 'rgba(0, 151, 178, 0.35)', color: '#9ee7f2', background: 'rgba(0, 151, 178, 0.12)' }}
          >
            {t('legal.last_updated')} {LAST_UPDATED_LABEL}
          </span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            {t('legal.privacy_title')}
          </h1>
          <p className="mt-4 text-lg md:text-xl text-white/75 max-w-3xl">
{t('legal.privacy_subtitle')}
          </p>
        </div>
      </header>

      <main className="relative z-10 pb-24 px-6 lg:px-8">
        <article className="max-w-3xl mx-auto rounded-2xl border border-white/20 bg-white/5 backdrop-blur-xl shadow-2xl p-6 sm:p-8 md:p-10 space-y-10">
          <section>
            <p className="text-white/80 leading-7">
              Welcome to Hairchitech ("we", "our", "us"). This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our website, applications, and services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s1_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.privacy_s1_intro')}
            </p>
            <div className="mt-4 space-y-4 text-white/80">
              <div>
<h3 className="text-lg font-semibold text-white">{t('legal.privacy_s1a_title')}</h3>
                <p className="mt-2">{t('legal.privacy_s1a_intro')}</p>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>{t('legal.privacy_s1a_item1')}</li>
                  <li>{t('legal.privacy_s1a_item2')}</li>
                  <li>{t('legal.privacy_s1a_item3')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('legal.privacy_s1b_title')}</h3>
                <p className="mt-2">{t('legal.privacy_s1b_intro')}</p>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>{t('legal.privacy_s1b_item1')}</li>
                  <li>{t('legal.privacy_s1b_item2')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('legal.privacy_s1c_title')}</h3>
                <p className="mt-2">{t('legal.privacy_s1c_intro')}</p>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>{t('legal.privacy_s1c_item1')}</li>
                  <li>{t('legal.privacy_s1c_item2')}</li>
                  <li>{t('legal.privacy_s1c_item3')}</li>
                  <li>{t('legal.privacy_s1c_item4')}</li>
                  <li>{t('legal.privacy_s1c_item5')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('legal.privacy_s1d_title')}</h3>
                <p className="mt-2">
{t('legal.privacy_s1d_body')}
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s2_title')}</h2>
            <p className="mt-3 text-white/80">{t('legal.privacy_s2_intro')}</p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s2_item1')}</li>
              <li>{t('legal.privacy_s2_item2')}</li>
              <li>{t('legal.privacy_s2_item3')}</li>
              <li>{t('legal.privacy_s2_item4')}</li>
              <li>{t('legal.privacy_s2_item5')}</li>
              <li>{t('legal.privacy_s2_item6')}</li>
              <li>{t('legal.privacy_s2_item7')}</li>
              <li>{t('legal.privacy_s2_item8')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s3_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.privacy_s3_intro')}
            </p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s3_item1')}</li>
              <li>{t('legal.privacy_s3_item2')}</li>
              <li>{t('legal.privacy_s3_item3')}</li>
            </ul>
            <h3 className="mt-4 text-lg font-semibold text-white">{t('legal.privacy_s3_storage_title')}</h3>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s3_storage1')}</li>
              <li>{t('legal.privacy_s3_storage2')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s4_title')}</h2>
            <p className="mt-3 text-white/80">{t('legal.privacy_s4_intro1')}</p>
            <p className="mt-2 text-white/80">{t('legal.privacy_s4_intro2')}</p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s4_item1')}</li>
              <li>{t('legal.privacy_s4_item2')}</li>
              <li>{t('legal.privacy_s4_item3')}</li>
            </ul>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s4_note')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s5_title')}</h2>
            <p className="mt-3 text-white/80">{t('legal.privacy_s5_intro')}</p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s5_item1')}</li>
              <li>{t('legal.privacy_s5_item2')}</li>
              <li>{t('legal.privacy_s5_item3')}</li>
            </ul>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s5_note')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s6_title')}</h2>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s6_intro')}
            </p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s6_item1')}</li>
              <li>{t('legal.privacy_s6_item2')}</li>
              <li>{t('legal.privacy_s6_item3')}</li>
            </ul>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s6_note')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s7_title')}</h2>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s7_intro')}
            </p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s7_item1')}</li>
              <li>{t('legal.privacy_s7_item2')}</li>
              <li>{t('legal.privacy_s7_item3')}</li>
              <li>{t('legal.privacy_s7_item4')}</li>
            </ul>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s7_note')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s8_title')}</h2>
            <p className="mt-3 text-white/80">
{t('legal.privacy_s8_intro')}
            </p>
            <ul className="mt-2 list-disc pl-6 space-y-1 text-white/80">
              <li>{t('legal.privacy_s8_item1')}</li>
              <li>{t('legal.privacy_s2_item8')}</li>
              <li>{t('legal.privacy_s8_item3')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s9_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.privacy_s9_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s10_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.privacy_s10_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s11_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.privacy_s11_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.privacy_s12_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.privacy_s12_intro')}
            </p>
            <p className="mt-3 text-white/80">
              Email: [your email]
            </p>
            <p className="mt-1 text-white/80">
              Website:
              {' '}
              <a href="https://hairchitech.com" className="underline hover:no-underline" style={{ color: '#9ee7f2' }}>
                https://hairchitech.com
              </a>
            </p>
          </section>
        </article>
      </main>
      <div className="relative z-10">
        <PublicFooter />
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
